/**
 * Frontend error reporter — sends an uncaught exception or a manually
 * captured error to `POST /v1/client-errors` so it lands in the
 * `error_events` table.
 *
 * Designed for two call sites:
 *   1. <ErrorBoundary>'s componentDidCatch — passes the React
 *      componentStack alongside the error.
 *   2. Manual reporting from catch blocks where the user-visible
 *      state is broken but rendering survived (e.g. failed fetch).
 *
 * Best-effort: a delivery failure must NOT cascade into another
 * thrown error. We swallow all fetch errors silently — there's
 * nothing useful the UI can do with them.
 *
 * Rate-limited at the server (30/min/IP). Client-side we additionally
 * cap to 1 report per (scope, message) within 60 seconds so a render
 * loop doesn't flood the network tab.
 */

import { apiRequest } from "../../shared/lib/api-client.ts";

const RECENT_TTL_MS = 60_000;
const RECENT_CAP = 200;
const recentReports = new Map<string, number>();

function rememberReport(key: string): boolean {
  const now = Date.now();
  const lastAt = recentReports.get(key);
  if (lastAt && now - lastAt < RECENT_TTL_MS) return false;
  if (recentReports.size > RECENT_CAP) {
    // Crude eviction — drop the oldest half if we ever blow past the cap.
    const entries = [...recentReports.entries()].sort((a, b) => a[1] - b[1]);
    for (const [k] of entries.slice(0, RECENT_CAP / 2)) {
      recentReports.delete(k);
    }
  }
  recentReports.set(key, now);
  return true;
}

export interface ReportClientErrorOptions {
  /** Bucket for grouping. Component name, feature slug, etc. */
  scope: string;
  /** The error (or arbitrary object) that was thrown/caught. */
  error: unknown;
  /** Optional React component stack (only set by ErrorBoundary). */
  componentStack?: string;
  /** Optional extra context the caller wants to attach. */
  extra?: Record<string, unknown>;
}

function extractMessageAndStack(error: unknown): { message: string; stack: string | null } {
  if (error instanceof Error) {
    return { message: error.message || error.name, stack: error.stack ?? null };
  }
  if (typeof error === "string") {
    return { message: error, stack: null };
  }
  try {
    return { message: JSON.stringify(error), stack: null };
  } catch {
    return { message: String(error), stack: null };
  }
}

export function reportClientError(options: ReportClientErrorOptions): void {
  const { scope, error, componentStack, extra } = options;
  const { message, stack } = extractMessageAndStack(error);
  const dedupeKey = `${scope}::${message.slice(0, 120)}`;
  if (!rememberReport(dedupeKey)) return;

  // Clamp to the server's Zod caps (url 2000, userAgent 500). Exceeding
  // either makes the whole report fail validation with a 400 the
  // fire-and-forget caller never sees — and a deep link with a long
  // query string is exactly the kind of page whose crash matters.
  const url = typeof window !== "undefined" ? window.location.href.slice(0, 2_000) : undefined;
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined;

  // Fire-and-forget. Never throws.
  void apiRequest("/v1/client-errors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      // Every field clamped to the server's Zod caps. Overrunning ANY of
      // them fails validation for the whole payload, and this is a
      // fire-and-forget call, so the 400 is never seen and the report is
      // simply lost. `extractMessageAndStack` JSON-stringifies arbitrary
      // thrown values, and a deep component tree produces a long
      // componentStack, so these are reachable in practice.
      scope: scope.slice(0, 200),
      message: message.slice(0, 2_000),
      stack: stack?.slice(0, 12_000) ?? stack,
      componentStack: componentStack?.slice(0, 8_000),
      url,
      userAgent,
      extra,
    }),
  }).catch(() => {
    // Intentional — error reporter failures must not cascade.
  });
}
